import { useContext, useEffect, useState } from "react";

import "react-toastify/dist/ReactToastify.min.css";
import { ToastContainer } from "react-toastify";

import BreadCrumb from "./BreadCrumb";
import GoldenRod from "./GoldenRod";
import Header from "./header/Header";
import LoadingSvg2 from "./LoadingSvg2";
import OpenEnrollmentDao from "../dao/OpenEnrollmentDao";
import RbA from "./RbA";
import StudentInfoDao from "../dao/StudentInfoDao";
import StudentsContainer from "./StudentsContainer";
import T from "./translations/Translations";
import WelcomeText from "./WelcomeText";

import { getEpoch, getTodayWithTime } from "../utils/DateFormatter";
import { GlobalContext } from "./contextProvider/ContextProvider";

import "../styles/global/Global.scss";
import SaeDao from "../dao/SaeDao";

/**
 * Guardian Dashboard Main
 * @name Main
 * @constructor
 * @return {JSX.Element}
 */
const Main = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const {
        guardianState,
        oeActionControls,
        oeActiveDistrictWindow,
        oeDistrictWindows,
        oeSchoolYearKey,
        saeActionControls,
        schoolYearDto, // OE has its own active school year!!!!
        studentsIc,
        studentsOe,
        thruDashboard,
        token,
        userDetails,
        username
    } = state || {};

    const [broadcast, setBroadcast] = useState(false);
    const [loader, setLoader] = useState(true);
    const [oeTemps, setOeTemps] = useState(null);

    const allowedRolesArray = ["EXTERNAL_GUARDIAN", "GUARDIAN"];

    // Broadcast message components if Broadcast is true
    const goldenRodBody = "";
    const goldenRodHeadline = "";

    /**
     * Init this useEffect if and when we have BE services for broadcast message on a User scale (non-student-specific)
     * This is a placeholder for now.
     */
    useEffect(() => {
        if (!token || !username) {
            return;
        }

        /* eslint-disable-next-line no-unused-vars */
        const fetchBroadcastMessage = () => {
            try {
                const options = {
                    action: "broadcastMessageRead",
                    params: {
                        activeDate: getTodayWithTime()
                    },
                    token,
                    username
                };
                const response = OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload && payload.length > 0) {
                        setBroadcast(payload);
                    }
                }
            } catch (error) {
                console.error(`Unable to retrieve Guardian Status for user ${userDetails.uid}:\n`, error);
            }
        };

        /**
         * fetchBroadcastMessage();
         */
    }, [token, userDetails, username]);

    /**
     * If the user is a guardian set guardianState (true). If an external guardian set guardianState (false).
     */
    useEffect(() => {
        if (guardianState || !userDetails) {
            return;
        }

        /**
         * @name getGuardianState
         * @description Get the user's guardian status
         * @async
         */
        const getGuardianState = () => {
            try {
                const { roleDtos } = userDetails;
                const roleMatch = roleDtos.some((obj) => obj.role === "GUARDIAN");
                dispatch({
                    type: "GuardianState",
                    guardianState: roleMatch
                });
            } catch (error) {
                console.error(`Unable to retrieve Guardian Status for user ${userDetails.displayName}:\n`, error);
            }
        };

        getGuardianState();
    }, [dispatch, guardianState, userDetails]);

    /**
     * Get guardian's IC students info
     */
    useEffect(() => {
        if ((studentsIc && thruDashboard) || guardianState === undefined || !schoolYearDto || !token || !username) {
            return;
        }

        /**
         * @name fetchStudents
         * @description Get a list of all IC students for the school year and sort them oldest first
         * @async
         */
        const fetchStudents = async () => {
            try {
                if (guardianState) {
                    const options = {
                        action: "studentsByGuardianRead",
                        params: {
                            checkForActiveEnrollment: true
                        },
                        token,
                        username
                    };
                    const response = await StudentInfoDao(options);
                    if (response && response.data) {
                        const { payload } = response.data;
                        payload.sort((a, b) => {
                            if (getEpoch(a.birthdate) === getEpoch(b.birthdate)) {
                                if (a.lastName === b.lastName) {
                                    return a.firstName.localeCompare(b.firstName);
                                }

                                return a.lastName.localeCompare(b.lastName);
                            }

                            return getEpoch(a.birthdate) > getEpoch(b.birthdate) ? 1 : -1;
                        });

                        dispatch({
                            type: "StudentsIc",
                            studentsIc: payload
                        });
                    }
                } else {
                    dispatch({
                        type: "StudentsIc",
                        studentsIc: []
                    });
                }
                dispatch({
                    type: "ThruDashboard",
                    thruDashboard: true
                });
            } catch (error) {
                console.error(`Unable to retrieve Guardian's students:\n`, error);
            } finally {
                dispatch({
                    type: "StudentsFlag",
                    studentsFlag: {
                        IC: true
                    }
                });
            }
        };

        fetchStudents();
    }, [dispatch, guardianState, schoolYearDto, studentsIc, thruDashboard, token, username]);

    /**
     * Once we have the basic OE student list, get each oe student's details
     */
    useEffect(() => {
        if (studentsOe || !oeTemps || !token) {
            return;
        }

        /**
         * @name fetchStudentDetails
         * @description Get a list of all OE students for the school year and sort them oldest first
         * @async
         */
        const fetchStudentDetails = async () => {
            try {
                const detailPromises = oeTemps.map(async (student) => {
                    const options = {
                        action: "studentDetailsRead",
                        key: student.key,
                        token
                    };
                    try {
                        const response = await OpenEnrollmentDao(options);
                        if (response && response.data) {
                            const { payload } = response.data;

                            return payload;
                        }
                    } catch (error) {
                        console.error(`Unable to retrieve details for student ${student.key}:\n`, error);
                    }
                });
                // Resolve all promises and filter out null results
                const studentDetails = (await Promise.all(detailPromises)).filter(Boolean);
                dispatch({
                    type: "StudentsOe",
                    studentsOe: studentDetails
                });
            } catch (error) {
                console.error("Unable to retrieve Open Enrollment details:\n", error);
            } finally {
                dispatch({
                    type: "StudentsFlag",
                    studentsFlag: {
                        OE: true
                    }
                });
            }
        };

        fetchStudentDetails();
    }, [dispatch, oeTemps, studentsOe, token]);

    /**
     * Get guardian's OE students along with their basic info.
     */
    useEffect(() => {
        if (studentsOe || oeTemps || !token || !userDetails || !oeSchoolYearKey) {
            return;
        }

        /**
         * @name fetchStudents
         * @description Get a list of all OE students for the school year and sort them oldest first
         * @async
         */
        const fetchStudents = async () => {
            try {
                const options = {
                    action: "studentsByParentGuidRead",
                    params: {
                        parentGuid: userDetails.uid
                    },
                    schoolYearKey: oeSchoolYearKey,
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    payload.sort((a, b) => {
                        if (getEpoch(a.birthdate) === getEpoch(b.birthdate)) {
                            if (a.lastName === b.lastName) {
                                return a.firstName.localeCompare(b.firstName);
                            }

                            return a.lastName.localeCompare(b.lastName);
                        }

                        return getEpoch(a.birthdate) > getEpoch(b.birthdate) ? 1 : -1;
                    });
                    setOeTemps(payload);
                }
            } catch (error) {
                console.error(`Unable to retrieve Guardian's students for school year ${oeSchoolYearKey}:\n`, error);
            } finally {
                dispatch({
                    type: "StudentsFlag",
                    studentsFlag: {
                        OE: true
                    }
                });
            }
        };

        fetchStudents();
    }, [dispatch, oeSchoolYearKey, oeTemps, studentsOe, token, userDetails]);

    /**
     * Set District Windows and the Active District Window if it exists
     */
    useEffect(() => {
        if (oeDistrictWindows || !oeSchoolYearKey || !token) {
            return;
        }

        /**
         * @name fetchDistictWindows
         * @description Get a list of all District Windows for the school year
         * @async
         */
        const fetchDistictWindows = async () => {
            try {
                const options = {
                    action: "allDistrictWindowsRead",
                    schoolYearKey: oeSchoolYearKey,
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    payload.sort((a, b) => {
                        return a.enrollmentRound > b.enrollmentRound ? 1 : -1;
                    });
                    dispatch({
                        type: "OeDistrictWindows",
                        oeDistrictWindows: payload
                    });
                    // get the activeDistrictWindow
                    const epochNow = getEpoch(getTodayWithTime());
                    const active = payload.filter(
                        (obj) => getEpoch(obj.startDate) <= epochNow && getEpoch(obj.endDate) >= epochNow
                    );
                    if (active && active.length > 0) {
                        dispatch({
                            type: "OeActiveDistrictWindow",
                            oeActiveDistrictWindow: active[0]
                        });
                    }
                }
            } catch (error) {
                console.error(`Unable to retrieve Active District Window for school year ${oeSchoolYearKey}:\n`, error);
            }
        };

        fetchDistictWindows();
    }, [dispatch, oeDistrictWindows, oeSchoolYearKey, token]);

    /**
     * Get a list of all Action Control windows for the school year
     */
    useEffect(() => {
        if (oeActionControls || !token || !oeActiveDistrictWindow || !oeSchoolYearKey) {
            return;
        }

        // if we don't have an active district window, use the school year
        const syk = oeActiveDistrictWindow ? oeActiveDistrictWindow?.schoolYearKey : oeSchoolYearKey;

        /**
         * @name fetchActionControls
         * @description Get a list of all Action Control windows for the school year
         * @async
         */
        const fetchActionControls = async () => {
            try {
                const options = {
                    action: "allActionControlsRead",
                    params: {
                        schoolYearKey: syk
                    },
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "OeActionControls",
                        oeActionControls: payload
                    });
                }
            } catch (error) {
                console.error(`Unable to retrieve OE Action Controls for school year ${syk}:\n`, error);
            }
        };

        fetchActionControls();
    }, [oeActionControls, oeActiveDistrictWindow, dispatch, oeSchoolYearKey, token]);

    /**
     * Get a list of all Action Control windows for the school year
     */
    useEffect(() => {
        if (saeActionControls || !schoolYearDto || !token) {
            return;
        }

        /**
         * @name fetchActionControls
         * @description Get a list of all Action Control windows for the school year
         * @async
         */
        const fetchActionControls = async () => {
            try {
                const options = {
                    action: "saeActionControlsRead",
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                const response = await SaeDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "SaeActionControls",
                        saeActionControls: payload
                    });
                }
            } catch (error) {
                console.error(`Unable to retrieve SAE Action Controls:\n`, error);
            }
        };

        fetchActionControls();
    }, [dispatch, saeActionControls, schoolYearDto, token]);

    /**
     * Get the current school year
     */
    useEffect(() => {
        if (oeSchoolYearKey || !token) {
            return;
        }

        /**
         * @name fetchSchoolYear
         * @description Get the current school year
         * @async
         */
        const fetchOeSchoolYearKey = async () => {
            try {
                const options = {
                    action: "schoolYearRead",
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "OeSchoolYearKey",
                        oeSchoolYearKey: payload
                    });
                }
            } catch (error) {
                console.error("Unable to retrieve the current school year:\n", error);
            }
        };

        fetchOeSchoolYearKey();
    }, [dispatch, oeSchoolYearKey, token]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <div style={{ display: "flex", flexDirection: "column" }}>
                <ToastContainer />
                <Header />
                {/* DCSD Guardian Dashboard */}
                <BreadCrumb label={T({ key: "Breadcrumb" })} />
                <div className="gutter-96">
                    {!guardianState && <WelcomeText />}
                    {broadcast && <GoldenRod headline={goldenRodHeadline} bodyText={goldenRodBody} />}
                    <StudentsContainer loader={loader} setLoader={setLoader} />
                </div>
                {loader && <LoadingSvg2 />}
            </div>
        </RbA>
    );
};

export default Main;
